'use client';

import type { SearchResult, ContentEncoding } from 'conversational-search-client';
import { telemetryGaugeAction } from 'core-events/actions';
import React, { type JSX, useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@videoblocks/design-system';
import { DownloadBottom } from '@videoblocks/react-icons';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import { downloadStockItemAction, downloadStockItemPreviewAction } from '../../_actions/downloadActions';
import { DEFAULT_DOWNLOAD_ERROR_MESSAGE, useChatSessionContext } from '../../_context/SessionContext';
import { formatFileSize } from '../../_utils';
import { type VideoFormat, VideoDownloadPopover } from './VideoDownloadPopover';

const logger = LoggerFactory.create('convSearchVideoResultCard');

interface VideoResultCardProps {
    result: SearchResult;
}

function formatDuration(totalSeconds: number): string {
    const floored = Math.floor(totalSeconds);
    const minutes = Math.floor(floored / 60);
    const seconds = floored % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function getResolutionLabel(resolution: number): string {
    if (resolution >= 2160) return '4K';
    if (resolution >= 720) return 'HD';
    if (resolution > 0) return 'SD';
    return '';
}

function buildFormats(encodings: ContentEncoding[]): VideoFormat[] {
    const best = new Map<VideoFormat['formatName'], Omit<VideoFormat, 'formatName'> & { resolution: number }>();

    for (const enc of encodings) {
        const { format, resolution = 0, fileSizeBytes } = enc;
        const displayFormat = format.toUpperCase();
        const resolutionLabel = getResolutionLabel(resolution);
        const key = `${resolutionLabel}${displayFormat}`;
        const existing = best.get(key);

        // if we have multiple encodings with the same resolution label and format, keep the one with the highest actual resolution
        if (!existing || resolution > existing.resolution) {
            best.set(key, {
                resolution,
                displayFormat,
                resolutionLabel,
                fileSize: formatFileSize(fileSizeBytes),
            });
        }
    }

    return (
        [...best.entries()]
            // don't return format with numeric resolution--only used for ranking/deduping
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .map(([formatName, { resolution, ...format }]) => ({
                formatName,
                ...format,
            }))
            .sort((a, b) => {
                if (a.resolutionLabel === b.resolutionLabel) return 0;
                if (a.resolutionLabel === '4K') return -1;
                if (b.resolutionLabel === '4K') return 1;
                if (a.resolutionLabel === 'HD') return -1;
                if (b.resolutionLabel === 'HD') return 1;
                return 0;
            })
    );
}

export function VideoResultCard({ result }: VideoResultCardProps): JSX.Element {
    const { canDownloadVideo, setDownloadError } = useChatSessionContext();
    const durationSec = result.duration;
    const href = result.detailsUrl;
    const isPortrait = result.orientation === 'portrait';
    const videoRef = useRef<HTMLVideoElement>(null);
    const [showPopover, setShowPopover] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const formats = useMemo<VideoFormat[]>(
        () => buildFormats(result.downloadEncodings ?? []),
        [result.downloadEncodings],
    );

    const handleMouseEnter = useCallback((): void => {
        const video = videoRef.current;
        if (!video) return;

        video.play().catch((error: unknown) => {
            // Retry once on AbortError (browser autoplay policy race)
            if (error instanceof DOMException && error.name === 'AbortError') {
                videoRef.current?.play().catch(() => {});
            }
        });
    }, []);

    const handleMouseLeave = useCallback((): void => {
        const video = videoRef.current;
        if (!video) return;

        video.pause();
        video.currentTime = 0;
    }, []);

    const handleDownloadClick = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        setShowPopover(true);
    }, []);

    const handleFormatSelect = useCallback(
        async (formatName: string): Promise<void> => {
            setIsDownloading(true);
            try {
                const downloadResult = await downloadStockItemAction(
                    result.stockblocksId,
                    formatName,
                    result.contentClass,
                );
                if (downloadResult.ok) {
                    void telemetryGaugeAction('conv-search.item.download', result.contentId);
                    window.location.href = downloadResult.url;
                } else {
                    setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
                }
            } catch (error: unknown) {
                logger.warn(
                    { error, stockblocksId: result.stockblocksId, formatName, contentClass: result.contentClass },
                    'Download action threw on client',
                );
                setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
            } finally {
                setIsDownloading(false);
                setShowPopover(false);
            }
        },
        [result.stockblocksId, result.contentClass, result.contentId, setDownloadError],
    );

    const handlePreviewDownloadClick = useCallback(
        async (e: React.MouseEvent): Promise<void> => {
            e.preventDefault();
            e.stopPropagation();
            if (isDownloading) return;

            setIsDownloading(true);
            try {
                const downloadResult = await downloadStockItemPreviewAction(result.stockblocksId, result.contentClass);
                if (downloadResult.ok) {
                    void telemetryGaugeAction('conv-search.preview.downloaded', result.contentId);
                    window.location.href = downloadResult.url;
                } else {
                    setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
                }
            } catch (error: unknown) {
                logger.warn(
                    { error, stockblocksId: result.stockblocksId, contentClass: result.contentClass },
                    'Preview download action threw on client',
                );
                setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
            } finally {
                setIsDownloading(false);
            }
        },
        [isDownloading, result.stockblocksId, result.contentClass, result.contentId, setDownloadError],
    );

    const handleClosePopover = useCallback((): void => {
        setShowPopover(false);
    }, []);

    return (
        <div
            className={twMerge('relative size-full overflow-hidden rounded-md bg-gray-20', !showPopover && 'group')}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <a
                href={href}
                onClick={() => {
                    void telemetryGaugeAction('conv-search.item.clickthrough', result.contentId);
                }}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 z-0"
                aria-label={result.title}
            >
                <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    poster={result.thumbnailUrl}
                    muted
                    loop
                    autoPlay={false}
                    preload="metadata"
                    controls={false}
                    controlsList="nodownload"
                >
                    <source src={result.previewUrl} type="video/mp4" />
                </video>
            </a>

            <div
                className="absolute inset-0 rounded-md pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 40%)',
                }}
            />
            <div className="flex space-x-2 items-center absolute top-0 left-0 px-4 pt-4 w-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                {durationSec != null && (
                    <div className="py-0.5 px-1.5 bg-white rounded text-sm leading-5 text-gray-900">
                        <time dateTime={`PT${durationSec}S`}>{formatDuration(durationSec)}</time>
                    </div>
                )}
                <h3 className="truncate font-semibold text-sm text-white m-0 leading-5">{result.title}</h3>
            </div>

            <DownloadAction
                isPortrait={isPortrait}
                label={canDownloadVideo ? 'Download Video' : 'Download Preview'}
                disabled={isDownloading}
                onClick={
                    canDownloadVideo
                        ? handleDownloadClick
                        : (e: React.MouseEvent): void => {
                              void handlePreviewDownloadClick(e);
                          }
                }
            />

            {canDownloadVideo && showPopover && (
                <VideoDownloadPopover
                    formats={formats}
                    isDownloading={isDownloading}
                    onSelectFormat={handleFormatSelect}
                    onClose={handleClosePopover}
                />
            )}
        </div>
    );
}

/* ——— Subcomponents —————————————————————————————————————————————————————————— */

interface DownloadActionProps {
    isPortrait: boolean;
    label: string;
    disabled: boolean;
    onClick: (e: React.MouseEvent) => void;
}

const POSITION_CLASS_NAME =
    'absolute left-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto z-[1]';
const SHARED_BUTTON_CLASS_NAME =
    'flex items-center text-white bg-gray-900 rounded-full border-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';

function DownloadAction({ isPortrait, label, disabled, onClick }: DownloadActionProps): JSX.Element {
    // A labelled pill is too wide for the narrow vertical card, so collapse to an icon button and
    // surface the label through a tooltip instead.
    if (isPortrait) {
        return (
            <div className={POSITION_CLASS_NAME}>
                <Tooltip
                    title={label}
                    variant="dark"
                    trigger={
                        <button
                            type="button"
                            aria-label={label}
                            onClick={onClick}
                            disabled={disabled}
                            className={twMerge(SHARED_BUTTON_CLASS_NAME, 'justify-center w-10 h-10')}
                        >
                            <DownloadBottom fill="white" width={14} height={14} />
                        </button>
                    }
                />
            </div>
        );
    }

    return (
        <div className={POSITION_CLASS_NAME}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className={twMerge(SHARED_BUTTON_CLASS_NAME, 'gap-1.5 text-xs font-medium px-4 py-3')}
            >
                <DownloadBottom fill="white" width={14} height={14} />
                {label}
            </button>
        </div>
    );
}
